import { deriveKey, encryptString, decryptString } from '../crypto'
import type { StorageConnector, StorageConnectorId } from './types'

const DB_NAME = 'byokapp-storage'
const DB_VERSION = 1
const CREDS_STORE = 'credentials'
const CREDS_KEY = 'google-drive'

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file'
const DRIVE_FILES_URL = 'https://www.googleapis.com/drive/v3/files'
const DRIVE_UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart'
const FOLDER_MIME = 'application/vnd.google-apps.folder'
const APP_FOLDER_NAME = 'byokapp.com'

export interface TokenProvider {
  requestToken(
    onSuccess: (token: string, email: string) => void,
    onError?: (error: string) => void,
  ): void
}

interface StoredCredential {
  id: string
  email: string
  iv: number[]
  ciphertext: number[]
  salt: number[]
}

interface GoogleDriveOptions {
  tokenProvider: TokenProvider
  idbFactory?: IDBFactory
  passphrase?: string
  fetchFn?: typeof fetch
}

function openStorageDb(idbFactory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = idbFactory.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('handles')) {
        db.createObjectStore('handles', { keyPath: 'key' })
      }
      if (!db.objectStoreNames.contains(CREDS_STORE)) {
        db.createObjectStore(CREDS_STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function saveCredential(
  idbFactory: IDBFactory,
  passphrase: string,
  token: string,
  email: string,
): Promise<void> {
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const key = await deriveKey(passphrase, salt)
  const { iv, ciphertext } = await encryptString(key, JSON.stringify({ token, email }))

  const record: StoredCredential = {
    id: CREDS_KEY,
    email,
    iv: Array.from(iv),
    ciphertext: Array.from(new Uint8Array(ciphertext)),
    salt: Array.from(salt),
  }

  const db = await openStorageDb(idbFactory)
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CREDS_STORE, 'readwrite')
    const req = tx.objectStore(CREDS_STORE).put(record)
    req.onsuccess = () => {
      resolve()
      db.close()
    }
    req.onerror = () => {
      reject(req.error)
      db.close()
    }
  })
}

async function loadCredential(
  idbFactory: IDBFactory,
  passphrase: string,
): Promise<{ token: string; email: string } | null> {
  const db = await openStorageDb(idbFactory)
  const record = await new Promise<StoredCredential | null>((resolve, reject) => {
    const tx = db.transaction(CREDS_STORE, 'readonly')
    const req = tx.objectStore(CREDS_STORE).get(CREDS_KEY)
    req.onsuccess = () => {
      resolve((req.result as StoredCredential | undefined) ?? null)
      db.close()
    }
    req.onerror = () => {
      reject(req.error)
      db.close()
    }
  })

  if (!record) return null

  const salt = new Uint8Array(record.salt)
  const iv = new Uint8Array(record.iv)
  const ciphertext = new Uint8Array(record.ciphertext).buffer
  const key = await deriveKey(passphrase, salt)
  const plaintext = await decryptString(key, iv, ciphertext)
  return JSON.parse(plaintext) as { token: string; email: string }
}

async function deleteCredential(idbFactory: IDBFactory): Promise<void> {
  const db = await openStorageDb(idbFactory)
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CREDS_STORE, 'readwrite')
    const req = tx.objectStore(CREDS_STORE).delete(CREDS_KEY)
    req.onsuccess = () => {
      resolve()
      db.close()
    }
    req.onerror = () => {
      reject(req.error)
      db.close()
    }
  })
}

export class GoogleDriveConnector implements StorageConnector {
  readonly id: StorageConnectorId = 'google-drive'

  private token: string | null = null
  private _accountEmail: string | null = null
  private folderId: string | null = null

  private readonly tokenProvider: TokenProvider
  // Lazily resolved so the class is safe to import in Node/test environments
  private readonly _idbFactoryOpt: IDBFactory | undefined
  private readonly passphrase: string
  private readonly fetchFn: typeof fetch

  private get idbFactory(): IDBFactory {
    return this._idbFactoryOpt ?? indexedDB
  }

  constructor(options: GoogleDriveOptions) {
    this.tokenProvider = options.tokenProvider
    this._idbFactoryOpt = options.idbFactory
    this.passphrase = options.passphrase ?? 'default-passphrase'
    this.fetchFn = options.fetchFn ?? fetch.bind(globalThis)
  }

  isConfigured(): boolean {
    return this.token !== null
  }

  get accountEmail(): string | null {
    return this._accountEmail
  }

  async init(): Promise<void> {
    const cred = await loadCredential(this.idbFactory, this.passphrase)
    if (cred) {
      this.token = cred.token
      this._accountEmail = cred.email
    }
  }

  async connect(): Promise<void> {
    const { token, email } = await new Promise<{ token: string; email: string }>(
      (resolve, reject) => {
        this.tokenProvider.requestToken(
          (t, e) => resolve({ token: t, email: e }),
          (err) => reject(new Error(err)),
        )
      },
    )

    this.token = token
    this._accountEmail = email
    await saveCredential(this.idbFactory, this.passphrase, token, email)
  }

  private async refreshToken(): Promise<void> {
    const { token, email } = await new Promise<{ token: string; email: string }>(
      (resolve, reject) => {
        this.tokenProvider.requestToken(
          (t, e) => resolve({ token: t, email: e }),
          (err) => reject(new Error(err)),
        )
      },
    )
    this.token = token
    this._accountEmail = email
    await saveCredential(this.idbFactory, this.passphrase, token, email)
  }

  private async ensureFolder(): Promise<string> {
    if (this.folderId) return this.folderId

    // Search for existing folder
    const searchUrl = `${DRIVE_FILES_URL}?q=${encodeURIComponent(`name='${APP_FOLDER_NAME}' and mimeType='${FOLDER_MIME}' and trashed=false`)}&fields=files(id)`
    const searchRes = await this.fetchFn(searchUrl, {
      headers: { Authorization: `Bearer ${this.token}` },
    })
    const searchData = (await searchRes.json()) as { files: { id: string }[] }

    if (searchData.files.length > 0) {
      this.folderId = searchData.files[0]!.id
      return this.folderId
    }

    // Create the folder
    const createRes = await this.fetchFn(DRIVE_FILES_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: APP_FOLDER_NAME, mimeType: FOLDER_MIME }),
    })
    const createData = (await createRes.json()) as { id: string }
    this.folderId = createData.id
    return this.folderId
  }

  async save(blob: Blob, filename: string): Promise<void> {
    if (!this.token) throw new Error('Google Drive not connected')

    try {
      await this.uploadFile(blob, filename)
    } catch (err) {
      if (err instanceof DriveAuthError) {
        await this.refreshToken()
        await this.uploadFile(blob, filename)
      } else {
        throw err
      }
    }
  }

  private async uploadFile(blob: Blob, filename: string): Promise<void> {
    const folderId = await this.ensureFolder()

    const metadata = JSON.stringify({
      name: filename,
      mimeType: 'image/png',
      parents: [folderId],
    })

    const boundary = 'byokapp_boundary_' + Date.now()
    const body = new Blob([
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
      metadata,
      `\r\n--${boundary}\r\nContent-Type: image/png\r\n\r\n`,
      blob,
      `\r\n--${boundary}--`,
    ])

    const res = await this.fetchFn(DRIVE_UPLOAD_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body,
    })

    if (res.status === 401) throw new DriveAuthError()
    if (!res.ok) throw new Error(`Drive upload failed: ${res.status}`)
  }

  async disconnect(): Promise<void> {
    this.token = null
    this._accountEmail = null
    this.folderId = null
    await deleteCredential(this.idbFactory)
  }
}

class DriveAuthError extends Error {
  constructor() {
    super('Drive auth expired')
  }
}

/** Default token provider using the Google Identity Services library (loaded via CDN). */
export class GisTokenProvider implements TokenProvider {
  private readonly getClientId: () => string
  private readonly scope: string

  // Accepts either a plain string (convenience) or a getter function (for lazy/dynamic IDs)
  constructor(getClientId: string | (() => string)) {
    this.getClientId = typeof getClientId === 'string' ? () => getClientId : getClientId
    this.scope = `${DRIVE_SCOPE} email profile`
  }

  requestToken(
    onSuccess: (token: string, email: string) => void,
    onError?: (error: string) => void,
  ): void {
    const clientId = this.getClientId()
    if (!clientId) {
      onError?.('Google Client ID not configured')
      return
    }

    const g = (
      globalThis as unknown as {
        google?: {
          accounts?: {
            oauth2?: { initTokenClient: (opts: unknown) => { requestAccessToken: () => void } }
          }
        }
      }
    ).google
    if (!g?.accounts?.oauth2) {
      onError?.('Google Identity Services not loaded')
      return
    }

    const client = g.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: this.scope,
      callback: (response: { access_token?: string; error?: string }) => {
        if (response.error ?? !response.access_token) {
          onError?.(response.error ?? 'Unknown OAuth error')
          return
        }
        // Fetch email via userinfo endpoint
        fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${response.access_token}` },
        })
          .then((r) => r.json() as Promise<{ email?: string }>)
          .then((info) => onSuccess(response.access_token!, info.email ?? ''))
          .catch(() => onSuccess(response.access_token!, ''))
      },
    })
    client.requestAccessToken()
  }
}
